import { useState } from 'react';
import { sendFeedback } from '@sentry/react';
import { IconBug } from 'components/Icons';
import './ReportIssueButton.scss';

interface ReportIssueButtonProps {
  className?: string;
}

export default function ReportIssueButton({ className = '' }: ReportIssueButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Submit feedback to Sentry
      sendFeedback({
        name: formData.name,
        email: formData.email,
        message: formData.message,
      });

      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });

      // Close modal after 2 seconds
      setTimeout(() => {
        setIsModalOpen(false);
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      setSubmitStatus('error');
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        className={`report-issue-button ${className}`}
        onClick={() => setIsModalOpen(true)}
        title="Report an issue or provide feedback"
      >
        <IconBug className="report-issue-icon" />
        Report an Issue
      </button>

      {isModalOpen && (
        <div className="report-issue-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="report-issue-modal" onClick={e => e.stopPropagation()}>
            <div className="report-issue-modal-header">
              <h2>Report an Issue</h2>
              <button className="report-issue-close-btn" onClick={() => setIsModalOpen(false)} aria-label="Close">
                ×
              </button>
            </div>

            {submitStatus === 'success' ? (
              <div className="report-issue-modal-body">
                <div className="report-issue-success-message">
                  <p>✓ Thank you! Your feedback has been submitted successfully.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="report-issue-form">
                <div className="report-issue-modal-body">
                  <div className="report-issue-form-group">
                    <label htmlFor="name">Name (optional)</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                    />
                  </div>

                  <div className="report-issue-form-group">
                    <label htmlFor="email">Email (optional)</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div className="report-issue-form-group">
                    <label htmlFor="message">
                      Message <span className="required">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Describe the issue or provide feedback..."
                      rows={5}
                      required
                    />
                  </div>

                  {submitStatus === 'error' && (
                    <div className="report-issue-error-message">
                      <p>Failed to submit feedback. Please try again.</p>
                    </div>
                  )}
                </div>

                <div className="report-issue-modal-footer">
                  <button
                    type="button"
                    className="report-issue-cancel-btn"
                    onClick={() => setIsModalOpen(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="report-issue-submit-btn"
                    disabled={isSubmitting || !formData.message.trim()}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
